import React, { useContext, useEffect, useMemo, useState } from "react";
import { Box, Button, Card, Grid, Modal, TextField, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { equalTo, onValue, orderByChild, query, ref } from "firebase/database";
import moment from "moment";
import CircularLoading from "../components/CircularLoading";
import AlertDialog from "../components/AlertDialog";
import { colors } from "../components/Theme/WebTheme";
import { FONT_FAMILY, SECONDORY_COLOR, formatCurrency, getBookingStatusLabel, getCommissionAmountDisplay, getEstimatedEarningsDisplay, getFinalBidAmount, getFleetAdminDisplayName, getFleetCompanyType, labelText } from "../common/sharedFunctions";
import { api, FirebaseContext } from "common";
import { canDoAction, showActionPermissionDenied } from "common/src/actions/userPermissions";

const offerList = (offers) => {
  if (!offers) return [];
  const list = Array.isArray(offers) ? offers.filter(Boolean) : Object.values(offers).filter(Boolean);
  return list.filter((offer) => offer.status !== "cancelled");
};

const objectLabel = (value) => {
  if (!value) return "";
  if (typeof value === "string" || typeof value === "number") return value.toString();
  return value.name || value.carType || value.vehicleTypeName || value.label || "";
};

const getBookingDate = (booking) => (
  booking.bookingDate ||
  booking.tripdate ||
  booking.createdAt ||
  booking.created_at ||
  booking.date ||
  ""
);

const formatDateTime = (value) => {
  if (!value) return "";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("lll") : value;
};

const getBidAmount = (offer) => (
  offer.trip_cost ||
  offer.bidAmount ||
  offer.amount ||
  offer.offerAmount ||
  offer.price ||
  ""
);

const getOfferDate = (offer) => (
  offer.bidDate ||
  offer.bidTime ||
  offer.createdAt ||
  offer.created_at ||
  offer.updatedAt ||
  offer.date ||
  ""
);

const getDriverOfferName = (offer) => (
  offer.driver_name ||
  offer.driverName ||
  offer.name ||
  offer.displayName ||
  ""
);

const getBookingAddressText = (booking, pointKey, flatKey) => {
  const point = booking && booking[pointKey] ? booking[pointKey] : {};
  return point.add || point.description || point.address || booking[flatKey] || "";
};

const driverOffersSummary = (offers, settings, emptyText) => {
  const list = offerList(offers);
  if (list.length === 0) return emptyText;
  return list.slice(0, 3).map((offer, index) => {
    const name = getDriverOfferName(offer) || `Offer ${index + 1}`;
    const amount = getBidAmount(offer);
    const date = getOfferDate(offer);
    const parts = [name];
    if (amount !== "") parts.push(formatCurrency(amount, settings));
    if (date) parts.push(formatDateTime(date));
    return parts.join(" - ");
  }).join("\n");
};

const fleetOffersSummary = (offers, settings, emptyText) => {
  const list = offerList(offers);
  if (list.length === 0) return emptyText;
  return list.slice(0, 3).map((offer) => {
    const amount = getBidAmount(offer);
    const date = getOfferDate(offer);
    const parts = [
      "Transport Company Offer",
      getFleetAdminDisplayName(offer),
      getFleetCompanyType(offer)
    ];
    if (amount !== "") parts.push(formatCurrency(amount, settings));
    if (date) parts.push(formatDateTime(date));
    return parts.filter(Boolean).join(" - ");
  }).join("\n");
};

const getOwnFleetOffer = (booking, uid) => {
  const offer = booking && booking.fleetOffers && uid ? booking.fleetOffers[uid] : null;
  return offer && offer.status !== "cancelled" ? offer : null;
};

const getPreferredFleetAdminId = (booking) => (
  booking && (booking.preferredFleetAdminId || booking.favoriteTranspoId || booking.favoriteTranspoFleetAdmin || "")
);

const getPreferredDriverId = (booking) => (
  booking && (booking.preferredDriverId || booking.favoriteDriver || booking.favoriteTranspoDriver || "")
);

const hasRequestedDrivers = (booking) => {
  if (!booking || !booking.requestedDrivers) return false;
  if (Array.isArray(booking.requestedDrivers)) {
    return booking.requestedDrivers.filter(Boolean).length > 0;
  }
  if (typeof booking.requestedDrivers === "object") {
    return Object.keys(booking.requestedDrivers).some((key) => !!booking.requestedDrivers[key]);
  }
  return !!booking.requestedDrivers;
};

const isPreferredFleetAdminBooking = (booking, uid) => {
  const preferredFleetAdminId = getPreferredFleetAdminId(booking);
  return !!(preferredFleetAdminId && uid && fieldMatchesUid(preferredFleetAdminId, uid));
};

const normalizeVehicleType = (value) => {
  const raw = typeof value === "string" || typeof value === "number"
    ? value
    : (value && (value.name || value.carType || value.vehicleType || value.vehicle_type || value.vehicleTypeName || value.carTypeName || value.requestedVehicleType || value.label)) || "";
  const normalized = `${raw}`
    .toLowerCase()
    .trim()
    .replace(/footer|foot/g, "ft")
    .replace(/\s+/g, " ")
    .replace(/[^a-z0-9]/g, "");
  if (!normalized) return "";
  if (/(20)(ft)/.test(normalized) || normalized.indexOf("20ft") >= 0) return "20ft";
  if (/(40)(ft)/.test(normalized) || normalized.indexOf("40ft") >= 0) return "40ft";
  if (/(10)(ft)/.test(normalized) || normalized.indexOf("10ft") >= 0) return "10ft";
  if (normalized.indexOf("l300") >= 0 || normalized.indexOf("fbvan") >= 0 || normalized.indexOf("vanaluminum") >= 0 || normalized === "fb" || normalized === "van") return "l300fbvan";
  if (normalized.indexOf("truck3tons") >= 0 || normalized.indexOf("3tons") >= 0 || normalized.indexOf("3ton") >= 0) return "truck3tons";
  if (normalized.indexOf("wingvan") >= 0) return "wingvan";
  return normalized;
};

const getBookingVehicleKey = (booking) => normalizeVehicleType(booking && (booking.carType || booking.vehicleType || booking.vehicle_type || booking.requestedVehicleType || booking.vehicleTypeName || booking.carDetails));

const getCarVehicleKey = (car) => normalizeVehicleType(car && (car.carType || car.vehicleType || car.vehicle_type || car.vehicleTypeName || car.carTypeName || car.name || car.cartype || car.type));

const isTruthyOrMissing = (value) => (
  value === undefined || value === null || value === "" || value === true || value === "true" || value === 1 || value === "1"
);

const isVisibleBidCarUsable = (car) => !!(car && isTruthyOrMissing(car.active) && isTruthyOrMissing(car.approved) && isTruthyOrMissing(car.carApproved));

const fieldMatchesUid = (fieldValue, uid) => {
  if (!fieldValue || !uid) return false;
  const normalizedUid = `${uid}`.toLowerCase().trim();
  if (Array.isArray(fieldValue)) {
    return fieldValue.some((value) => fieldMatchesUid(value, uid));
  }
  if (typeof fieldValue === "object") {
    return Object.keys(fieldValue).some((key) => fieldValue[key] && `${key}`.toLowerCase().trim() === normalizedUid);
  }
  return `${fieldValue}`.toLowerCase().trim() === normalizedUid;
};

const hasMatchingFleetAdminCar = (booking, uid, cars) => {
  const bookingVehicleKey = getBookingVehicleKey(booking);
  const carList = Array.isArray(cars) ? cars : (cars && typeof cars === "object" ? Object.values(cars) : []);
  if (!bookingVehicleKey || !uid) return false;
  return carList.some((car) => (
    isVisibleBidCarUsable(car) &&
    fieldMatchesUid(car.fleetadmin || car.fleetAdmin || car.fleetadminId || car.fleetAdminId, uid) &&
    getCarVehicleKey(car) === bookingVehicleKey
  ));
};

const canShowOpenBidBooking = (booking, role, uid, cars) => {
  if (api.isFleetAdminPrivateClientBooking(booking)) return false;
  if (!booking || booking.deliveryWithBid !== true || booking.status !== "NEW") return false;
  if (role === "admin") return true;
  if (role !== "fleetadmin") return false;
  const hasMatchingVehicle = hasMatchingFleetAdminCar(booking, uid, cars);
  const preferredFleetAdminId = getPreferredFleetAdminId(booking);
  if (preferredFleetAdminId) return fieldMatchesUid(preferredFleetAdminId, uid) && hasMatchingVehicle;
  if (fieldMatchesUid(booking.requestedFleetAdmins, uid)) return hasMatchingVehicle;
  if (getPreferredDriverId(booking) || hasRequestedDrivers(booking)) return false;
  return hasMatchingVehicle;
};

export default function BookingOrdersForBid() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const firebase = useContext(FirebaseContext);
  const { submitFleetBid, cancelFleetBid, fetchCars } = api;
  const settings = useSelector((state) => state.settingsdata.settings);
  const auth = useSelector((state) => state.auth);
  const carlistdata = useSelector((state) => state.carlistdata);
  const role = auth.profile && auth.profile.usertype;
  const uid = auth.profile && auth.profile.uid;
  const canCreateBid = canDoAction({ role, moduleKey: "bookingOrdersForBid", action: "create", accessPermissions: settings && settings.accessPermissions, fallback: true });
  const canEditBid = canDoAction({ role, moduleKey: "bookingOrdersForBid", action: "edit", accessPermissions: settings && settings.accessPermissions, fallback: true });
  const canDeleteBid = canDoAction({ role, moduleKey: "bookingOrdersForBid", action: "delete", accessPermissions: settings && settings.accessPermissions, fallback: true });
  const label = (key, fallback) => labelText(t, key, fallback);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bidAmount, setBidAmount] = useState("");
  const [bidNote, setBidNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ open: false, msg: "" });

  useEffect(() => {
    if (role === "fleetadmin" && fetchCars && (!carlistdata || !carlistdata.cars)) {
      dispatch(fetchCars());
    }
  }, [dispatch, fetchCars, role, carlistdata && carlistdata.cars]);

  useEffect(() => {
    if (!firebase || !firebase.database || !role) return undefined;
    if (role !== "admin" && role !== "fleetadmin") {
      setBookings([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const openBidQuery = query(firebase.bookingMarketplaceListRef || ref(firebase.database, "bookingMarketplace"), orderByChild("status"), equalTo("NEW"));
    const unsubscribe = onValue(openBidQuery, (snapshot) => {
      const data = snapshot.val() || {};
      const openBookings = Object.keys(data)
        .map((key) => ({
          ...data[key],
          id: key,
          pickupAddress: data[key].pickupArea || getBookingAddressText(data[key], "pickup", "pickupAddress"),
          dropAddress: data[key].deliveryArea || getBookingAddressText(data[key], "drop", "dropAddress")
        }))
        .filter((booking) => canShowOpenBidBooking(booking, role, uid, carlistdata && carlistdata.cars))
        .reverse();
      setBookings(openBookings);
      setLoading(false);
    }, () => {
      setBookings([]);
      setLoading(false);
    });

    return unsubscribe;
  }, [firebase, role, uid, carlistdata && carlistdata.cars]);

  const rows = useMemo(() => (
    bookings.map((booking) => ({
      ...booking,
      driverOffersCount: offerList(booking.driverOffers).length,
      fleetOffersCount: offerList(booking.fleetOffers).length,
      ownFleetOffer: getOwnFleetOffer(booking, uid),
      isPreferredFleetAdmin: isPreferredFleetAdminBooking(booking, uid),
      hasMatchingFleetAdminVehicle: role === "fleetadmin" ? hasMatchingFleetAdminCar(booking, uid, carlistdata && carlistdata.cars) : true
    }))
  ), [bookings, uid, role, carlistdata && carlistdata.cars]);

  const openBidModal = (booking) => {
    const ownOffer = getOwnFleetOffer(booking, uid);
    if (ownOffer ? !canEditBid : !canCreateBid) {
      showActionPermissionDenied();
      return;
    }
    if (!canShowOpenBidBooking(booking, role, uid, carlistdata && carlistdata.cars)) {
      setAlert({ open: true, msg: label("fleet_bid_vehicle_mismatch", "This booking is not available for your fleet vehicle type.") });
      return;
    }
    setSelectedBooking(booking);
    setBidAmount(ownOffer && ownOffer.amount !== undefined ? ownOffer.amount.toString() : "");
    setBidNote(ownOffer && ownOffer.note ? ownOffer.note : "");
  };

  const closeBidModal = () => {
    if (saving) return;
    setSelectedBooking(null);
    setBidAmount("");
    setBidNote("");
  };

  const submitBid = () => {
    const ownOffer = getOwnFleetOffer(selectedBooking, uid);
    if (ownOffer ? !canEditBid : !canCreateBid) {
      showActionPermissionDenied();
      return;
    }
    const amount = parseFloat(bidAmount);
    if (isNaN(amount) || amount <= 0) {
      setAlert({ open: true, msg: label("fleet_bid_amount_required", "Bid amount must be greater than 0.") });
      return;
    }
    if (!canShowOpenBidBooking(selectedBooking, role, uid, carlistdata && carlistdata.cars)) {
      setAlert({ open: true, msg: label("fleet_bid_vehicle_mismatch", "This booking is not available for your fleet vehicle type.") });
      closeBidModal();
      return;
    }
    setSaving(true);
    dispatch(submitFleetBid({
      bookingId: selectedBooking.id,
      amount,
      note: bidNote
    })).then(() => {
      setSaving(false);
      setAlert({ open: true, msg: label("fleet_bid_saved", "Fleet bid saved.") });
      closeBidModal();
    }).catch((error) => {
      setSaving(false);
      setAlert({ open: true, msg: error && error.message ? error.message : label("fleet_bid_save_failed", "Unable to save fleet bid.") });
    });
  };

  const cancelBid = (booking) => {
    if (!canDeleteBid) {
      showActionPermissionDenied();
      return;
    }
    if (!window.confirm(label("cancel_fleet_bid_confirm", "Cancel this fleet bid?"))) return;
    setSaving(true);
    dispatch(cancelFleetBid({ bookingId: booking.id })).then(() => {
      setSaving(false);
      setAlert({ open: true, msg: label("fleet_bid_cancelled", "Fleet bid cancelled.") });
    }).catch((error) => {
      setSaving(false);
      setAlert({ open: true, msg: error && error.message ? error.message : label("fleet_bid_cancel_failed", "Unable to cancel fleet bid.") });
    });
  };

  const renderFleetAdminAction = (row) => {
    if (role !== "fleetadmin") return null;
    if (row.status !== "NEW") return null;
    if (!row.hasMatchingFleetAdminVehicle) return null;
    if (row.ownFleetOffer) {
      return (
        <Box>
          <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 13, marginBottom: 8 }}>
            {formatCurrency(row.ownFleetOffer.amount || 0, settings)}
          </Typography>
          {canEditBid ? (
            <Button
              variant="contained"
              size="small"
              disabled={saving}
              style={{ marginRight: 8, marginBottom: 8, backgroundColor: colors.Action_Button_Back, color: colors.Header_Background, fontFamily: FONT_FAMILY, textTransform: "none" }}
              onClick={() => openBidModal(row)}
            >
              {label("edit_bid", "Edit Bid")}
            </Button>
          ) : null}
          {canDeleteBid ? (
            <Button
              variant="contained"
              size="small"
              disabled={saving}
              style={{ marginBottom: 8, backgroundColor: colors.RED, color: colors.WHITE, fontFamily: FONT_FAMILY, textTransform: "none" }}
              onClick={() => cancelBid(row)}
            >
              {label("cancel_bid", "Cancel Bid")}
            </Button>
          ) : null}
        </Box>
      );
    }
    if (!canCreateBid) return null;
    return (
      <Button
        variant="contained"
        size="small"
        disabled={saving}
        style={{ backgroundColor: colors.Action_Button_Back, color: colors.Header_Background, fontFamily: FONT_FAMILY, textTransform: "none" }}
        onClick={() => openBidModal(row)}
      >
        {label("submit_fleet_bid", "Submit Fleet Bid")}
      </Button>
    );
  };

  const columns = [
    { key: "reference", label: label("booking_ref", "Booking Number / Reference"), minWidth: 170, render: (row) => row.reference || row.bookingNumber || row.id || "" },
    { key: "customer_name", label: label("customer_name", "Customer Name"), minWidth: 180 },
    { key: "shipmentType", label: label("shipment_type", "Shipment Type"), minWidth: 160, render: (row) => row.shipmentType || row.deliveryType || "" },
    { key: "serviceType", label: label("service_type", "Service Type"), minWidth: 160, render: (row) => row.serviceType || row.service_type || "" },
    { key: "carType", label: label("car_type", "Vehicle Type"), minWidth: 170, render: (row) => objectLabel(row.carType) },
    { key: "pickupAddress", label: label("pickup_address", "Pickup Address"), minWidth: 240, render: (row) => row.pickupAddress || "" },
    { key: "dropAddress", label: label("delivery_address", "Delivery Address"), minWidth: 240, render: (row) => row.dropAddress || "" },
    { key: "bookingDate", label: label("booking_date", "Booking Date / Created Date"), minWidth: 190, render: (row) => formatDateTime(getBookingDate(row)) },
    { key: "status", label: label("booking_status_web", "Status"), minWidth: 150, render: (row) => getBookingStatusLabel(row, role, t, auth.profile) },
    { key: "bidAmount", label: label("bid_amount", "Bid Amount"), minWidth: 150, render: (row) => {
      const amount = getFinalBidAmount(row);
      return amount !== null && amount !== undefined && amount !== "" ? formatCurrency(amount, settings) : label("waiting_for_bid_acceptance", "Waiting for bid acceptance");
    } },
    { key: "commissionAmount", label: label("commission_amount", "Commission Amount"), minWidth: 170, render: (row) => getCommissionAmountDisplay(row, settings, t) },
    { key: "estimatedEarnings", label: label("estimated_earnings", "Estimated Earnings"), minWidth: 180, render: (row) => getEstimatedEarningsDisplay(row, settings, t) },
    { key: "driverOffersCount", label: label("driver_offers_count", "Driver Offers Count"), minWidth: 150 },
    { key: "fleetOffersCount", label: label("fleet_offers_count", "Fleet Offers Count"), minWidth: 150 },
    { key: "driverOffersSummary", label: label("driver_offers_summary", "Driver Offers Summary"), minWidth: 260, render: (row) => driverOffersSummary(row.driverOffers, settings, label("no_offers_yet", "No offers yet")) },
    { key: "fleetOffersSummary", label: label("fleet_offers_summary", "Fleet Offers Summary"), minWidth: 280, render: (row) => fleetOffersSummary(row.fleetOffers, settings, label("no_offers_yet", "No offers yet")) },
    ...(role === "fleetadmin" ? [{
      key: "favoriteTranspo",
      label: label("favorite_transpo", "Favorite Transpo"),
      minWidth: 180,
      render: (row) => row.isPreferredFleetAdmin ? (
        <Typography style={{ display: "inline-block", padding: "3px 10px", borderRadius: 12, backgroundColor: colors.Action_Button_Back, color: colors.Header_Background, fontFamily: FONT_FAMILY, fontWeight: 700, fontSize: 12 }}>
          {label("preferred_by_customer", "Preferred by Customer")}
        </Typography>
      ) : ""
    }] : []),
    ...(role === "fleetadmin" ? [{ key: "fleetBidAction", label: label("fleet_bid", "Fleet Bid"), minWidth: 260, render: renderFleetAdminAction }] : []),
    {
      key: "action",
      label: label("action", "Action"),
      minWidth: 150,
      render: (row) => (
        <Button
          variant="contained"
          size="small"
          style={{ backgroundColor: colors.Action_Button_Back, color: colors.Header_Background, fontFamily: FONT_FAMILY, textTransform: "none" }}
          onClick={() => navigate(`/bookings/bookingdetails/${row.id}`)}
        >
          {label("booking_details", "View Details")}
        </Button>
      )
    }
  ];

  if (loading) {
    return <CircularLoading />;
  }

  return (
    <Card style={{ margin: 20, padding: 18, borderRadius: 8 }}>
      <Typography variant="h5" style={{ fontFamily: FONT_FAMILY, fontWeight: "bold", marginBottom: 12 }}>
        {label("booking_orders_for_bid", "Booking Orders for Bid")}
      </Typography>
      <Box style={{ width: "100%", overflowX: "auto", border: `1px solid ${SECONDORY_COLOR}` }}>
        <Box component="table" style={{ minWidth: role === "fleetadmin" ? 3000 : 2740, width: "100%", borderCollapse: "collapse", fontFamily: FONT_FAMILY }}>
          <Box component="thead">
            <Box component="tr">
              {columns.map((column) => (
                <Box
                  component="th"
                  key={column.key}
                  style={{
                    minWidth: column.minWidth,
                    backgroundColor: colors.Table_Header,
                    border: `1px solid ${SECONDORY_COLOR}`,
                    padding: 10,
                    fontSize: 13,
                    textAlign: "left",
                    whiteSpace: "normal"
                  }}
                >
                  {column.label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {rows.length === 0 ? (
              <Box component="tr">
                <Box component="td" colSpan={columns.length} style={{ padding: 24, textAlign: "center", border: `1px solid ${SECONDORY_COLOR}` }}>
                  {label("no_bid_orders", "No bid orders found")}
                </Box>
              </Box>
            ) : rows.map((row) => (
              <Box component="tr" key={row.id} style={{ backgroundColor: row.isPreferredFleetAdmin ? "rgba(255, 193, 7, 0.08)" : "transparent" }}>
                {columns.map((column) => (
                  <Box
                    component="td"
                    key={`${row.id}-${column.key}`}
                    style={{
                      minWidth: column.minWidth,
                      border: `1px solid ${SECONDORY_COLOR}`,
                      padding: 10,
                      fontSize: 13,
                      verticalAlign: "top",
                      whiteSpace: column.key.indexOf("Summary") !== -1 ? "pre-line" : "normal",
                      wordBreak: "break-word"
                    }}
                  >
                    {column.render ? column.render(row) : (row[column.key] || "")}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Modal open={!!selectedBooking} onClose={closeBidModal}>
        <Grid
          container
          spacing={2}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "min(520px, calc(100vw - 32px))",
            backgroundColor: colors.WHITE,
            border: `1px solid ${SECONDORY_COLOR}`,
            borderRadius: 8,
            padding: 20
          }}
        >
          <Grid item xs={12}>
            <Typography variant="h6" style={{ fontFamily: FONT_FAMILY, fontWeight: "bold" }}>
              {selectedBooking && getOwnFleetOffer(selectedBooking, uid) ? label("edit_bid", "Edit Bid") : label("submit_fleet_bid", "Submit Fleet Bid")}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={label("bid_amount", "Bid Amount")}
              type="number"
              value={bidAmount}
              onChange={(event) => setBidAmount(event.target.value)}
              inputProps={{ min: 0, step: "0.01" }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label={label("remarks", "Remarks")}
              value={bidNote}
              onChange={(event) => setBidNote(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} style={{ textAlign: "right" }}>
            <Button
              variant="contained"
              disabled={saving}
              style={{ marginRight: 10, backgroundColor: colors.RED, color: colors.WHITE, fontFamily: FONT_FAMILY, textTransform: "none" }}
              onClick={closeBidModal}
            >
              {label("cancel", "Cancel")}
            </Button>
            <Button
              variant="contained"
              disabled={saving}
              style={{ backgroundColor: colors.Action_Button_Back, color: colors.Header_Background, fontFamily: FONT_FAMILY, textTransform: "none" }}
              onClick={submitBid}
            >
              {label("save", "Save")}
            </Button>
          </Grid>
        </Grid>
      </Modal>
      <AlertDialog open={alert.open} onClose={() => setAlert({ open: false, msg: "" })}>
        {alert.msg}
      </AlertDialog>
    </Card>
  );
}
